import React from 'react';
import { Toaster, ToastPosition } from 'react-hot-toast';
import { HEADER_HEIGHT } from '@/theme';

interface ZuulToasterProps {
  customContainerStyle?: React.CSSProperties;
  duration?: number;
  style?: React.CSSProperties;
  position?: ToastPosition;
}

/**
 * App-wide toast host. Mounted once in DefaultLayout — react-hot-toast renders every toast in
 * every mounted <Toaster>, so a second mount duplicates them.
 *
 * Toasts enter from the top centre: it is the one screen region no view puts controls in, so a
 * toast never covers the thing the user just clicked. The container clears the sticky AppBar so
 * a toast never hides the breadcrumb or the backend-status pill.
 *
 * Colors come from MUI CSS variables rather than the theme object, so toasts follow the active
 * color scheme without this component re-rendering. Success and error toasts borrow the standard
 * MUI Alert tints: they replaced the inline `<Alert>` banners the detail pages used to show next
 * to their Save button, and keeping the same surface means a save still looks like a save.
 */
export const ZuulToaster: React.FC<ZuulToasterProps> = ({ customContainerStyle, duration, style, position }) => (
  <Toaster
    position={position || 'top-center'}
    toastOptions={{
      style: {
        background: 'var(--mui-palette-background-paper)',
        color: 'var(--mui-palette-text-primary)',
        border: '1px solid var(--mui-palette-divider)',
        ...style,
      },
      duration: duration || 4000,
      success: {
        style: {
          background: 'var(--mui-palette-Alert-successStandardBg)',
          color: 'var(--mui-palette-Alert-successColor)',
          border: '1px solid var(--mui-palette-success-main)',
        },
        iconTheme: {
          primary: 'var(--mui-palette-success-main)',
          secondary: 'var(--mui-palette-Alert-successStandardBg)',
        },
      },
      error: {
        style: {
          background: 'var(--mui-palette-Alert-errorStandardBg)',
          color: 'var(--mui-palette-Alert-errorColor)',
          border: '1px solid var(--mui-palette-error-main)',
        },
        iconTheme: {
          primary: 'var(--mui-palette-error-main)',
          secondary: 'var(--mui-palette-Alert-errorStandardBg)',
        },
      },
    }}
    containerStyle={{ top: HEADER_HEIGHT + 8, ...customContainerStyle }}
  />
);
